import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { useForm } from "react-hook-form";
import _ from "lodash";
import { Container, Form, Button, Icon, Divider } from "semantic-ui-react";

import * as USER_GROUPS from '../../../actions/userGroupsActions';
import * as ACTIVATION_RULES from '../../../actions/activationRulesActions';
import { useWhatsAppTemplate, WhatsAppTemplateModal, WhatsAppTemplateField } from '../../../components/whatsapp/WhatsAppTemplateSelector';

export function GroupMessageDetails(props) {
    const [state, setState] = useState({
        groupMessage: props.groupMessage,
        userGroup: props.group,
    });

    const {
        localTemplate,
        localInputValues,
        headerImage,
        isVerifying,
        mediaId,
        isModalOpen,
        hasImageHeader,
        extractUniquePlaceholders,
        handlePlaceholderChange,
        handleImageUpload,
        handleVerifyImage,
        handleTemplateSelection,
        confirmTemplateSelection,
        closeModal,
        addMediaIdToTemplate
    } = useWhatsAppTemplate();

    useEffect(() => {
        props.fetchWhatsAppTemplatesAction(props.tenant);
    }, [props.tenant]);

    const {
        control,
        register,
        handleSubmit,
        formState: { errors },
    } = useForm({
        mode: "all",
        reValidateMode: "onBlur",
    });

    const onSubmit = async (data) => {
        const templateToSend = addMediaIdToTemplate(
            state.groupMessage.whatsAppTemplate,
            state.groupMessage.whatsAppTemplateMediaId
        );

        let message = {
            title: data.title,
            body: data.body,
            whatsAppTemplate: templateToSend,
            whatsAppTemplatePlaceholderValues: state.groupMessage.whatsAppTemplatePlaceholderValues,
            whatsAppTemplateHeaderImage: state.groupMessage.whatsAppTemplateHeaderImage,
            whatsAppTemplateMediaId: state.groupMessage.whatsAppTemplateMediaId,
        }

        await props.sendMessageAction(message, state.userGroup, props.tenant);

        if (_.isFunction(props.onComplete)) {
            props.onComplete();
        }
    };

    const handleChange = (field, value) => {
        setState((prevState) => ({
            ...prevState,
            groupMessage: { ...prevState.groupMessage, [field]: value },
        }));
    };

    const handleTemplateSelect = (event, { value }) => {
        if (value == null) {
            setState((prevState) => ({
                ...prevState,
                groupMessage: { ...prevState.groupMessage, whatsAppTemplatePlaceholderValues: [] },
            }));
        } else {
            handleTemplateSelection(value);
        }
    };

    const onConfirmTemplate = (result) => {
        setState(state => ({
            ...state,
            groupMessage: {
                ...state.groupMessage,
                whatsAppTemplate: result.template,
                whatsAppTemplatePlaceholderValues: result.placeholderValues,
                whatsAppTemplateHeaderImage: result.headerImage,
                whatsAppTemplateMediaId: result.mediaId
            }
        }));
    };

    const _buildMessageTitle = () => {
        return (
            <Form.Field className="full-width-field">
                <label>Message Title: </label>
                <input
                    name="title"
                    className={errors.title ? "invalid-input-field" : ""}
                    {...register("title", { required: true, maxLength: 128 })}
                    placeholder="Message Title"
                    value={state.groupMessage.message?.title || ""}
                    readOnly={!props.isNew}
                    onChange={(e) => handleChange("message", { ...state.groupMessage.message, title: e.target.value })}
                />
                {errors.title && errors.title.type === "required" && (
                    <p className="validation-error">* Please enter a message title</p>
                )}
                {errors.title && errors.title.type === "maxLength" && (
                    <p className="validation-error">
                        * Message title may not be more than 128 characters long
                    </p>
                )}
            </Form.Field>
        );
    };

    const _buildMessageBody = () => {
        return (
            <Form.Field className="full-width-field">
                <label>Message Body: </label>
                <textarea
                    style={{ color: "DarkGrey" }}
                    name="body"
                    className={errors.body ? "invalid-input-field" : ""}
                    {...register("body", { required: true })}
                    placeholder="Message Body"
                    value={state.groupMessage?.message.body || ""}
                    readOnly={!props.isNew}
                    onChange={(e) => handleChange("message", { ...state.groupMessage.message, body: e.target.value })}
                />
                {errors.body && errors.body.type === "required" && (
                    <p className="validation-error">* Please enter a message body</p>
                )}
            </Form.Field>
        );
    };

    return (
        <Container>
            <Form
                size="small"
                onSubmit={handleSubmit(onSubmit)}
                style={{ paddingBottom: "20px" }}
            >
                <WhatsAppTemplateField
                    control={control}
                    templates={props.whatsAppTemplates}
                    selectedTemplate={state.groupMessage.whatsAppTemplate}
                    placeholderValues={state.groupMessage.whatsAppTemplatePlaceholderValues}
                    headerImage={state.groupMessage.whatsAppTemplateHeaderImage}
                    onTemplateSelect={handleTemplateSelect}
                    onPlaceholderChange={handlePlaceholderChange}
                />

                <WhatsAppTemplateModal
                    isOpen={isModalOpen}
                    template={localTemplate}
                    headerImage={headerImage}
                    mediaId={mediaId}
                    isVerifying={isVerifying}
                    onClose={closeModal}
                    onConfirm={() => confirmTemplateSelection(onConfirmTemplate)}
                    onImageUpload={handleImageUpload}
                    onVerifyImage={() => handleVerifyImage(props.tenant.id, 'UserGroups/TemplateImages')}
                    onPlaceholderChange={handlePlaceholderChange}
                    extractPlaceholders={extractUniquePlaceholders}
                    hasImageHeader={hasImageHeader}
                />

                {_buildMessageTitle()}
                {_buildMessageBody()}
                <Divider />
                <Button.Group floated="right">
                    <Button
                        className="danger"
                        onClick={() => {
                            if (_.isEmpty(props.onComplete)) {
                                props.onComplete();
                            }
                        }}
                    >
                        <Icon name="remove" /> Cancel
                    </Button>
                    {props.isNew && (
                        <Button type="submit" className="success">
                            <Icon name="checkmark" /> Send
                        </Button>
                    )}
                </Button.Group>
            </Form>
        </Container>
    );
}

const mapStateToProps = (state, ownProps) => {
    let groupMessage = {};
    let isNew = false;
    let newMessage = {
        message: {
            title: "",
            body: "",
        },
    };

    if (ownProps.bdaction === "add") {
        groupMessage = newMessage;
        isNew = true;
    } else {
        groupMessage = _.isEmpty(ownProps.message) ? newMessage : ownProps.message;
        isNew = false;
    }

    return {
        userPermissions: _.isEmpty(state.authUserRoles.permissions) ? {} : state.authUserRoles.permissions,
        isPermissionsFetched: state.isPermissionsFetched === true,
        tenant: state.tenantManagement.activeTenant,
        isNew: isNew,
        groupMessage: groupMessage,
        whatsAppTemplates: _.isEmpty(state.activationRules.whatsAppTemplates) ? [] : Object.values(state.activationRules.whatsAppTemplates),
    };
};

export default connect(
    mapStateToProps,
    (dispatch) => ({
        sendMessageAction: (message, userGroup, tenant) => dispatch(USER_GROUPS.sendMessageAction(message, userGroup, tenant)),
        fetchWhatsAppTemplatesAction: (tenant) => dispatch(ACTIVATION_RULES.fetchWhatsAppTemplatesAction(tenant)),
    })
)(GroupMessageDetails);
